import { MoreVertical, Download, Copy, Edit, Trash2, Settings, PanelRight, MessageSquare, Flag, Bug, MessageSquareWarning, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ChatFeedbackCategory } from '@/lib/chat-feedback';
import { testIds } from '@/lib/test-ids';
import {
  QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome';

interface ConversationMoreMenuProps {
  conversationId?: string;
  onExport?: () => void;
  onCopyTranscript?: () => void;
  onCopyConversationId?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  onClearHistory?: () => void;
  onSettings?: () => void;
  onMoveToEditorTab?: () => void;
  onMoveToAgentPanel?: () => void;
  onFeedback?: (category?: ChatFeedbackCategory) => void;
  /** Warm path: nudge agent to proposeSoulUpdate for durable facts. */
  onLearnFromSession?: () => void;
  /** Show agent-issue shortcut when a conversation is active. */
  hasChatMessages?: boolean;
  /** Hide bulk clear-history (e.g. agent panel / sidebar-adjacent surfaces). */
  showClearHistory?: boolean;
  menuButtonTestId?: string;
  /** Agent-panel session scratch flyout (compact only). */
  sessionFilesCount?: number;
  sessionFilesOpen?: boolean;
  onSessionFilesOpenChange?: (open: boolean) => void;
}

export function ConversationMoreMenu({
  conversationId,
  onExport,
  onCopyTranscript,
  onCopyConversationId,
  onRename,
  onDelete,
  onClearHistory,
  onSettings,
  onMoveToEditorTab,
  onMoveToAgentPanel,
  onFeedback,
  onLearnFromSession,
  hasChatMessages = false,
  showClearHistory = true,
  menuButtonTestId,
  sessionFilesCount = 0,
  sessionFilesOpen = false,
  onSessionFilesOpenChange,
}: ConversationMoreMenuProps) {
  const hasActiveConversation = !!conversationId;
  const hasMoveTargets = !!(onMoveToEditorTab || onMoveToAgentPanel);
  const showBulkClearHistory = showClearHistory && !!onClearHistory;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          data-testid={menuButtonTestId}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
      >
        {hasActiveConversation && (
          <>
            {onCopyTranscript && (
              <DropdownMenuItem
                onClick={onCopyTranscript}
                data-testid={testIds.conversationMoreMenu.copyTranscript}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy transcript
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onExport}>
              <Download className="mr-2 h-4 w-4" />
              Export as Markdown
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onCopyConversationId}>
              <Copy className="mr-2 h-4 w-4" />
              Copy conversation ID
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onRename}>
              <Edit className="mr-2 h-4 w-4" />
              Rename Conversation
            </DropdownMenuItem>
            {onLearnFromSession && hasChatMessages ? (
              <DropdownMenuItem
                onClick={onLearnFromSession}
                data-testid={testIds.conversationMoreMenu.learnFromSession}
              >
                <Brain className="mr-2 h-4 w-4" />
                Learn from this session
              </DropdownMenuItem>
            ) : null}
            {onSessionFilesOpenChange ? (
              <DropdownMenuCheckboxItem
                checked={sessionFilesOpen}
                onCheckedChange={(checked) => onSessionFilesOpenChange(checked === true)}
                data-testid={testIds.conversationMoreMenu.sessionFiles}
              >
                Session files
                {sessionFilesCount > 0 ? (
                  <span className="ml-auto text-xs text-muted-foreground">{sessionFilesCount}</span>
                ) : null}
              </DropdownMenuCheckboxItem>
            ) : null}
          </>
        )}
        {hasMoveTargets && (
          <>
            {hasActiveConversation && <DropdownMenuSeparator />}
            {onMoveToEditorTab && (
              <DropdownMenuItem onClick={onMoveToEditorTab}>
                <PanelRight className="mr-2 h-4 w-4" />
                Move to editor tab
              </DropdownMenuItem>
            )}
            {onMoveToAgentPanel && (
              <DropdownMenuItem onClick={onMoveToAgentPanel}>
                <MessageSquare className="mr-2 h-4 w-4" />
                Move to agent panel
              </DropdownMenuItem>
            )}
          </>
        )}
        {onSettings && (
          <>
            {(hasActiveConversation || hasMoveTargets) && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={onSettings}>
              <Settings className="mr-2 h-4 w-4" />
              Chat settings
            </DropdownMenuItem>
          </>
        )}
        {onFeedback && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onFeedback('feedback')}
              data-testid={testIds.conversationMoreMenu.feedback}
            >
              <Flag className="mr-2 h-4 w-4" />
              Send feedback
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onFeedback('bug')}
              data-testid={testIds.conversationMoreMenu.reportBug}
            >
              <Bug className="mr-2 h-4 w-4" />
              Report a bug
            </DropdownMenuItem>
            {hasChatMessages ? (
              <DropdownMenuItem
                onClick={() => onFeedback('agent-issue')}
                data-testid={testIds.conversationMoreMenu.agentIssue}
              >
                <MessageSquareWarning className="mr-2 h-4 w-4" />
                Report chat agent issue
              </DropdownMenuItem>
            ) : null}
          </>
        )}
        {(showBulkClearHistory || (hasActiveConversation && onDelete)) && (
          <DropdownMenuSeparator />
        )}
        {showBulkClearHistory && (
          <DropdownMenuItem
            onClick={onClearHistory}
            className={QUIET_MENU_DESTRUCTIVE_ITEM_CLASS}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Clear All History
          </DropdownMenuItem>
        )}
        {hasActiveConversation && onDelete && (
          <DropdownMenuItem
            onClick={onDelete}
            className={QUIET_MENU_DESTRUCTIVE_ITEM_CLASS}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Conversation
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
